import type { NewProductDraftSnapshot } from "./newProduct/state";

/**
 * Authoritative, score-safe context for FDE turns on Create Context Mesh.
 *
 * The draft analyzer runs independently of the chat agent. Supplying its live
 * state on every turn prevents the agent from inferring processing state from
 * disabled buttons or from an older conversation turn.
 */
export function buildContextMeshDraftAwareness(
    draft: NewProductDraftSnapshot | null
): string {
    if (!draft) {
        return "CREATE CONTEXT MESH LIVE STATUS: The form is still loading. Do not claim that analysis is idle, complete, or unavailable.";
    }

    const analysis = draft.contextMeshAnalysis;
    const sources = draft.connectedSources.length > 0
        ? draft.connectedSources.join(", ")
        : "none";
    const common =
        `CREATE CONTEXT MESH LIVE STATUS (authoritative): analysis=${analysis.status}; ` +
        `connectedSources=${sources}; score=${analysis.score ?? "not available"}; tier=${analysis.tier ?? "not available"}. `;
    const repositoryImports = draft.repositoryImports ?? [];
    const repositoryStatus = repositoryImports.length === 0
        ? ""
        : "REPOSITORY IMPORT LIVE STATUS (authoritative): " + repositoryImports.map(job => {
            const fileProgress = job.filesCompleted != null || job.filesTotal != null
                ? `; files=${job.filesCompleted ?? "unknown"}/${job.filesTotal ?? "unknown"}`
                : "";
            const detail = job.error ?? job.message;
            return `${job.repositoryName} (${job.provider}): status=${job.status}; stage=${job.stage ?? "not available"}; progress=${job.progress}%${fileProgress}${detail ? `; detail=${detail}` : ""}`;
        }).join(" | ") + ". " +
        "When asked about repository or Git processing, report these exact current details, including chunking and embedding counts present in detail. Distinguish repository import from Context Mesh score analysis. Do not invent counts, stages, or an ETA. ";
    const integration = draft.integrationImport;
    const integrationStatus = !integration
        ? ""
        : `INTEGRATION IMPORT LIVE STATUS (authoritative): ${integration.sourceName} is ${integration.status} ${integration.itemType}; ` +
        `progress=${integration.current}/${integration.total}; selections=${integration.selections.join(", ") || "not available"}. ` +
        `When asked what is happening with ${integration.sourceName}, report these exact counts and status. ` +
        "Distinguish integration import from Context Mesh score analysis. Do not invent counts or an ETA. ";
    const fileImports = draft.fileImports ?? [];
    const fileStatus = fileImports.length === 0
        ? ""
        : "FILE IMPORT LIVE STATUS (authoritative): " + fileImports.map(file =>
            `${file.name}: stage=${file.stage}; progress=${file.progress}%${file.error ? `; error=${file.error}` : ""}`
        ).join(" | ") + ". When asked about file processing, report each exact stage and progress. " +
        "Distinguish file import from Context Mesh score analysis. Do not invent progress or an ETA. ";
    const liveStatus = common + repositoryStatus + integrationStatus + fileStatus;

    switch (analysis.status) {
        case "analyzing":
            return liveStatus +
                "The Context Mesh score is currently being calculated from the latest draft and connected content. " +
                "If the user asks what is happening or what they are waiting for, say that analysis is in progress, no action is required, and the score will update automatically when it completes. " +
                "Do not say nothing is processing, and do not estimate a completion time.";
        case "stale":
            return liveStatus +
                "The Context Mesh score is currently being recalculated. Any displayed score is the last completed score, not the final result for the latest draft. " +
                "If the user asks what they are waiting for, say that updated analysis is in progress and will refresh automatically. Do not estimate a completion time.";
        case "ready":
            return liveStatus +
                "Canonical analysis for the current saved draft is complete. Do not claim that score analysis is still running.";
        case "failed":
            return liveStatus +
                "The latest analysis failed. Say that plainly; do not claim it is still processing or that it completed successfully.";
        default:
            return liveStatus +
                "No canonical analysis result is currently available. Do not infer that work is running or complete from button state alone.";
    }
}
